import { Component } from '@theme/component';

/**
 * Product gallery — one main image, a row of thumbnails, and swipe/arrow
 * navigation between them.
 *
 * Navigation is index-based over the thumbs, which stay the single source
 * of truth for what image is current: arrows, swipes and thumb clicks all
 * funnel through the same #show() so the selected state can't drift.
 *
 * @typedef {Object} Refs
 * @property {HTMLImageElement} main
 * @property {HTMLButtonElement[]} thumbs
 *
 * @extends {Component<Refs>}
 */
class Gallery extends Component {
  requiredRefs = ['main'];

  /** @type {number} pointerdown x, NaN while no drag is live */
  #startX = NaN;
  #startY = NaN;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);

    // Swipe on the main image. Pointer events cover mouse drag and touch
    // with one listener set; the 40px threshold keeps taps and small
    // scroll-adjustments from turning the page.
    const { main } = this.refs;
    main.addEventListener('pointerdown', this.#onPointerDown);
    main.addEventListener('pointerup', this.#onPointerUp);
    main.addEventListener('pointercancel', this.#onPointerCancel);
    main.setAttribute('draggable', 'false');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);

    const { main } = this.refs;
    main.removeEventListener('pointerdown', this.#onPointerDown);
    main.removeEventListener('pointerup', this.#onPointerUp);
    main.removeEventListener('pointercancel', this.#onPointerCancel);
  }

  get #currentIndex() {
    const thumbs = this.refs.thumbs ?? [];
    const i = thumbs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    return i < 0 ? 0 : i;
  }

  /** @param {number} index wraps around both ends */
  #show(index) {
    const thumbs = this.refs.thumbs ?? [];
    if (thumbs.length < 2) return;

    const next = thumbs[(index + thumbs.length) % thumbs.length];
    for (const other of thumbs) {
      other.setAttribute('aria-selected', 'false');
      other.tabIndex = -1;
    }
    next.setAttribute('aria-selected', 'true');
    next.tabIndex = 0;

    const { main } = this.refs;
    main.src = next.dataset.full;
    main.srcset = next.dataset.fullSrcset || '';
    main.alt = next.dataset.fullAlt || '';
  }

  #onClick = (event) => {
    const arrow = event.target.closest('[data-gal-nav]');
    if (arrow) {
      this.#show(this.#currentIndex + Number(arrow.dataset.galNav));
      return;
    }

    const thumb = event.target.closest('[data-full]');
    if (!thumb) return;
    this.#show((this.refs.thumbs ?? []).indexOf(thumb));
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#startX = event.clientX;
    this.#startY = event.clientY;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const dx = event.clientX - this.#startX;
    const dy = event.clientY - this.#startY;
    this.#startX = NaN;
    this.#startY = NaN;
    if (!Number.isFinite(dx) || Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
    // Drag left → next image, matching the direction the content "moves".
    this.#show(this.#currentIndex + (dx < 0 ? 1 : -1));
  };

  #onPointerCancel = () => {
    this.#startX = NaN;
    this.#startY = NaN;
  };

  #onKeyDown = (event) => {
    if (!event.target.matches('[data-full]')) return;
    const thumbs = this.refs.thumbs ?? [];
    let index = this.#currentIndex;
    if (event.key === 'ArrowRight') index += 1;
    else if (event.key === 'ArrowLeft') index -= 1;
    else if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = thumbs.length - 1;
    else return;
    event.preventDefault();
    this.#show(index);
    thumbs[this.#currentIndex]?.focus({ preventScroll: true });
  };
}

if (!customElements.get('tt-gallery')) {
  customElements.define('tt-gallery', Gallery);
}
